const API_URL = process.env.REACT_APP_API_URL || '';

/** Guest marketing checkout (no Gallop account yet). */
/** Public: pending row exists for email → signed /register URL. */
export async function requestResumeSignup(email: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/stripe/resume-signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim() }),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((body as { error?: string }).error || 'Could not resume signup');
  }
  const data = body as { url?: string };
  if (!data.url) {
    throw new Error('No registration link returned');
  }
  return data.url;
}

export async function verifyResumeToken(token: string): Promise<{ ok: true; email: string } | { ok: false; error: string }> {
  const response = await fetch(`${API_URL}/api/stripe/verify-resume-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: (body as { error?: string }).error || 'Verification failed' };
  }
  const data = body as { ok?: boolean; email?: string };
  if (data.ok && typeof data.email === 'string') {
    return { ok: true, email: data.email };
  }
  return { ok: false, error: 'Invalid response from server' };
}

export async function createMarketingCheckoutSession(): Promise<string> {
  const response = await fetch(`${API_URL}/api/stripe/marketing-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to start checkout');
  }

  const data = await response.json();
  const url = data.url as string | undefined;
  if (!url) {
    throw new Error('No checkout URL returned');
  }
  return url;
}

export async function createBetfairCheckoutSession(email: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/stripe/betfair-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email: email.trim() }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to start checkout');
  }

  const data = await response.json();
  const url = data.url as string | undefined;
  if (!url) {
    throw new Error('No checkout URL returned');
  }
  return url;
}

export async function claimPendingSubscription(
  accessToken: string,
  checkoutSessionId?: string | null
): Promise<{ claimed: boolean; synced?: boolean }> {
  const response = await fetch(`${API_URL}/api/stripe/claim-subscription`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(
      checkoutSessionId ? { checkout_session_id: checkoutSessionId } : {}
    ),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to link subscription');
  }

  return response.json() as Promise<{ claimed: boolean; synced?: boolean }>;
}

export type SubscriptionInfo = {
  subscription_status: string | null;
  subscription_current_period_end: string | null;
  subscription_cancel_at_period_end: boolean;
};

export async function fetchSubscriptionInfo(accessToken: string): Promise<SubscriptionInfo> {
  const response = await fetch(`${API_URL}/api/stripe/subscription-info`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to load subscription');
  }

  return response.json() as Promise<SubscriptionInfo>;
}

export async function cancelTrialSubscription(accessToken: string): Promise<{
  ok: boolean;
  subscription_current_period_end: string | null;
  subscription_cancel_at_period_end: boolean;
  already_cancelled?: boolean;
}> {
  const response = await fetch(`${API_URL}/api/stripe/cancel-trial`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to cancel trial');
  }

  return response.json() as Promise<{
    ok: boolean;
    subscription_current_period_end: string | null;
    subscription_cancel_at_period_end: boolean;
    already_cancelled?: boolean;
  }>;
}

export async function createCheckoutSession(
  accessToken: string
): Promise<{ url?: string; claimed?: boolean }> {
  const response = await fetch(`${API_URL}/api/stripe/checkout-session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to start checkout');
  }

  const data = (await response.json()) as { url?: string; claimed?: boolean };
  if (data.claimed) {
    return { claimed: true, url: data.url || '/dashboard' };
  }
  const url = data.url;
  if (!url) {
    throw new Error('No checkout URL returned');
  }
  return { url };
}

export async function createBillingPortalSession(accessToken: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/stripe/portal-session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as {
      error?: string;
      subscribe_url?: string;
    };
    if (response.status === 409 && err.subscribe_url) {
      window.location.href = err.subscribe_url;
      return err.subscribe_url;
    }
    throw new Error(err.error || 'Failed to open billing portal');
  }

  const data = await response.json();
  const url = data.url as string | undefined;
  if (!url) {
    throw new Error('No portal URL returned');
  }
  return url;
}
